import { createToolParameters } from '@goat-sdk/core';
import { OrderType } from '@orderly.network/types';
import { z } from 'zod';

export class CancelAllOrderPendingParams extends createToolParameters(
  z.object({
    symbol: z
      .string()
      .describe(
        'The symbol of the asset used in the pending orders (allowed symbols are returned by tool get_allowed_symbols_mode_trade)'
      ),
  })
) {}

export class CancelOrderParams extends createToolParameters(
  z.object({
    order_id: z.number().describe('The ID of the order to cancel'),
    symbol: z
      .string()
      .describe(
        'The symbol of the asset used in the order (allowed symbols are returned by tool get_allowed_symbols_mode_trade)'
      ),
  })
) {}

export class CreateOrderParams extends createToolParameters(
  z.object({
    symbol: z
      .string()
      .describe('Symbol of the asset to trade (allowed symbols are returned by tool get_allowed_symbols_mode_trade)'),
    order_type: z
      .enum([
        OrderType.MARKET,
        OrderType.LIMIT,
        OrderType.IOC,
        OrderType.FOK,
        OrderType.POST_ONLY,
        OrderType.ASK,
        OrderType.BID,
      ])
      .describe(
        `Order type:
- MARKET: Matches until the full size is executed; if the quantity is too large or the matching price exceeds the limit (refer to price_range), the remaining quantity will be cancelled.
- LIMIT: Matches at the specified order_price; if the quantity is too large or the matching price exceeds the limit (refer to price_range), the remaining quantity will be cancelled.
- IOC: Matches as much as possible at the specified order_price; any unexecuted portion will be cancelled.
- FOK: The order is fully executed only if it can be completely matched at the order_price; otherwise, it is cancelled without any execution.
- POST_ONLY: If the order would execute any maker trades upon placement, it will be cancelled without execution.
- ASK: The order price is guaranteed to be the best ask price in the order book at the time of acceptance.
- BID: The order price is guaranteed to be the best bid price in the order book at the time of acceptance.`
      ),
    order_price: z
      .string()
      .optional()
      .describe('The price of the order; required for LIMIT, IOC, FOK, and POST_ONLY orders.'),
    order_quantity: z
      .string()
      .optional()
      .describe(
        `Order quantity in terms of the base currency (example BTC or SOL or ETH) of the pair (not in base units).
            For MARKET/BID/ASK orders in Futures mode, use order_quantity for both BUY and SELL orders.`
      ),
    order_amount: z.string().optional().describe('Order size in terms of the quote currency: USDC.'),
    visible_quantity: z
      .string()
      .optional()
      .describe(
        `Maximum quantity displayed on the order book. Defaults to order_quantity.
Negative values or values larger than order_quantity are not allowed. If set to 0, the order will be hidden from the order book.
Does not apply to MARKET/IOC/FOK orders since those orders are executed and cancelled immediately.`
      ),
    client_order_id: z
      .string()
      .optional()
      .describe(
        `Custom order ID, unique among open orders.
Orders with the same client_order_id are accepted only when the previous one is completed; otherwise, the order will be rejected.`
      ),
    side: z.enum(['BUY', 'SELL']).describe('The side of the order: BUY or SELL.'),
  })
) {}

export class GetOrdersParameters extends createToolParameters(
  z.object({
    symbol: z
      .string()
      .optional()
      .describe(
        'filter orders by symbol of the asset to trade (allowed symbols are returned by tool get_allowed_symbols_mode_trade)'
      ),
    side: z.enum(['BUY', 'SELL']).optional().describe('filter orders by side of the order: BUY or SELL'),
    order_type: z.enum(['MARKET', 'LIMIT']).optional().describe('filter orders by order type: MARKET or LIMIT'),
    status: z.enum(['INCOMPLETE', 'COMPLETED', 'REJECTED']).optional()
      .describe(`filter orders by order status: INCOMPLETE, COMPLETED, or REJECTED
        - INCOMPLETE: Order is open and or partially filled.
        - COMPLETED: Order is completely filled or cancelled.`),
    page: z
      .number()
      .optional()
      .describe(
        `Page number for paginated results. Defaults to 1.
        Each page contains 10 orders.`
      ),
    sort_by: z
      .enum(['CREATED_TIME_DESC', 'CREATED_TIME_ASC', 'UPDATED_TIME_DESC', 'UPDATED_TIME_ASC'])
      .optional()
      .describe(
        'Sort order: CREATED_TIME_DESC, CREATED_TIME_ASC, UPDATED_TIME_DESC, UPDATED_TIME_ASC, default is CREATED_TIME_DESC'
      ),
  })
) {}

export class ClosePositionParams extends createToolParameters(
  z.object({
    symbol: z
      .string()
      .describe('Symbol of the asset to trade (allowed symbols are returned by tool get_allowed_symbols_mode_trade)'),
  })
) {}

export class DepositParams extends createToolParameters(
  z.object({
    amount: z.string().describe('The amount of input tokens to deposit in base units'),
  })
) {}

export class GetPositionHistoryParams extends createToolParameters(
  z.object({
    // symbol: z
    //   .string()
    //   .optional()
    //   .describe(
    //     'filter positions by symbol of the asset to trade (allowed symbols are returned by tool get_allowed_symbols_mode_trade)'
    //   ),
    limit: z.number().default(10).describe('Maximum number of positions to return'),
  })
) {}

export class GetUSDCInfoParams extends createToolParameters(
  z.object({
    symbol: z.string().describe('Get info for USDC tokens supported on Mode Trade.'),
  })
) {}

export class WithdrawParams extends createToolParameters(
  z.object({
    amount: z.string().describe('The amount of input tokens to withdraw in base units'),
  })
) {}

export class GetOrderbookSnapshotParams extends createToolParameters(
  z.object({
    symbol: z
      .string()
      .describe('Symbol of the asset (allowed symbols are returned by tool get_allowed_symbols_mode_trade)'),
  })
) {}

export class GetBuildersUsersVolumesParams extends createToolParameters(
  z.object({
    start_date: z.string().describe('Start date for the volume data in YYYY-MM-DD format'),
    end_date: z.string().describe('End date for the volume data in YYYY-MM-DD format'),
    address: z
      .string()
      .optional()
      .describe('Address of the user to get volume data for, if not provided, all users will be returned'),
  })
) {}

export class GetBuildersLeaderboardParams extends createToolParameters(
  z.object({
    start_date: z.string().describe('Start date for the leaderboard in YYYY-MM-DD format'),
    end_date: z.string().describe('End date for the leaderboard in YYYY-MM-DD format'),
  })
) {}
